import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Building, Zap, Award, Truck, Mail, Phone } from 'lucide-react';

const ForBusiness: React.FC = () => {
  const { t, i18n } = useTranslation();

  // Determine locale-specific contacts
  const isEnglish = (i18n.language || i18n.resolvedLanguage || 'en').toLowerCase().startsWith('en');
  const whatsappNumber = isEnglish ? '61401461019' : '6281337329299';
  const whatsappLabel = isEnglish ? 'WhatsApp Tony (English)' : 'WhatsApp Nila (Bahasa)';

  const features = [
    {
      icon: <Award className="w-10 h-10 text-amber-400" />,
      title: t('forBusiness.value.quality.title'),
      description: t('forBusiness.value.quality.description'),
    },
    {
      icon: <Building className="w-10 h-10 text-amber-400" />,
      title: t('forBusiness.value.branding.title'),
      description: t('forBusiness.value.branding.description'),
    },
    {
      icon: <Truck className="w-10 h-10 text-amber-400" />,
      title: t('forBusiness.value.delivery.title'),
      description: t('forBusiness.value.delivery.description'),
    },
    {
      icon: <Zap className="w-10 h-10 text-amber-400" />,
      title: t('forBusiness.value.orders.title'),
      description: t('forBusiness.value.orders.description'),
    },
  ];

  const solutions = [
    {
      title: t('forBusiness.solutions.hotels.title'),
      description: t('forBusiness.solutions.hotels.description'),
      img: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    },
    {
      title: t('forBusiness.solutions.corporate.title'),
      description: t('forBusiness.solutions.corporate.description'),
      img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1632&q=80',
    },
    {
      title: t('forBusiness.solutions.cafe.title'),
      description: t('forBusiness.solutions.cafe.description'),
      img: 'https://images.unsplash.com/photo-1559925393-8be0ec4767c8?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    },
    {
      title: t('forBusiness.solutions.retail.title'),
      description: t('forBusiness.solutions.retail.description'),
      img: 'https://images.unsplash.com/photo-1570857502809-0818b874388e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80',
    },
  ];

  return (
    <div className="bg-gray-900 text-white">
      {/* Hero Section */}
      <header className="relative bg-gray-800 shadow-lg">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* You can add a specific logo for the business page or keep it consistent */}
          </div>
        </div>
      </header>
      
      <section 
        className="relative py-24 md:py-32 bg-cover bg-center" 
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1542826438-c33c477625a2?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1470&q=80')" }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"></div>
        <div className="relative container mx-auto px-4 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-serif font-extrabold text-white tracking-tight"
          >
            {t('forBusiness.hero.title')}
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mt-4 text-lg md:text-xl max-w-3xl mx-auto text-gray-300"
          >
            {t('forBusiness.hero.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-gray-800 rounded-xl shadow-lg hover:shadow-amber-400/20 transition-shadow duration-300"
              >
                <div className="flex justify-center mb-4">{feature.icon}</div>
                <h3 className="text-xl font-bold text-amber-400 mb-2">{feature.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 md:py-24 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-center text-white mb-12">
            {t('forBusiness.solutions.title')}
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {solutions.map((solution, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                viewport={{ once: true }}
                className="group relative overflow-hidden rounded-lg shadow-xl"
              >
                <img src={solution.img} alt={solution.title} className="w-full h-80 object-cover transform group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 p-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{solution.title}</h3>
                  <p className="text-gray-300">{solution.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="bg-gray-800 rounded-2xl p-8 md:p-12 lg:p-16 shadow-2xl">
            <div className="text-white max-w-3xl mx-auto text-center">
              <h3 className="text-3xl md:text-4xl font-serif font-bold mb-3">{t('forBusiness.cta.title')}</h3>
              <p className="text-gray-400 mb-6">{t('forBusiness.cta.altCta')}</p>
              <div className="flex flex-col items-center gap-4">
                <a href="mailto:admin@tncakesbali.com" className="inline-flex items-center gap-3 text-lg hover:text-amber-400 transition-colors">
                  <Mail className="w-6 h-6 text-amber-400" />
                  <span>admin@tncakesbali.com</span>
                </a>
                <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-lg hover:text-amber-400 transition-colors">
                  <Phone className="w-6 h-6 text-amber-400" />
                  <span>{whatsappLabel}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForBusiness;
